import { useState, useEffect, useRef } from 'react'
import { Footer, Nav } from './App'
// ═══════════════════════════════════════════════════════════════════════
// DATA MODEL — Future-proof: Add new collabs by adding objects below.
// Each collaboration is self-contained with its event, artists,
// conversations, and partners.
// ═══════════════════════════════════════════════════════════════════════

interface ArtistData {
  id: number
  name: string
  role: string
  bio: string
  highlights: string[]
  image: string | null // null = placeholder
  socials?: { platform: string; url: string }[]
}

interface ConversationData {
  id: number
  artist: string
  title: string
  description: string
  thumbnail: string | null
  youtubeUrl: string | null
  youtubeEmbedId: string | null
}

interface PartnerData {
  name: string
  logo: string | null  // null = placeholder
  placeholder: string  // Short initials for placeholder display
  url?: string
}

interface CollaborationEvent {
  id: number
  slug: string
  eventName: string
  tagline: string
  date: string
  venue: string
  city: string
  description: string
  bannerImage: string | null
  artists: ArtistData[]
  conversations: ConversationData[]
  partners: PartnerData[]
}

// ═══════════════════════════════════════════════════════════════════════
// COLLABORATION DATA
// To add a new collaboration, just add a new object to this array.
// The page will render all collabs automatically.
// ═══════════════════════════════════════════════════════════════════════

const COLLABORATIONS: CollaborationEvent[] = [
  // ───────────────────────────────────────────────────────────────────
  // COLLAB #1 — Raft (June 2026)
  // ───────────────────────────────────────────────────────────────────
  {
    id: 2,
    slug: 'raft-partnership-2026',
    eventName: "WD Forte'26 By THE RAFT, Bangalore",
    tagline: 'Community Venue Partnership — live stages, jam sessions, and local music community.',
    date: '26th & 27th June 2026',
    venue: 'The Raft',
    city: 'Bengaluru',
    description: "FretPractice is thrilled to partner with The Raft — one of Bengaluru's most beloved live music venues, tucked in the heart of Koramangala. Founded on a deep-rooted love for music, The Raft has built a home for artists and listeners alike, hosting everything from intimate jam sessions and open mics to full band showcases across genres. As a community partner, we share a common belief: that great music is built through consistent practice, real stages, and the right community around you. The Raft gives musicians exactly that.",
    bannerImage: '/images/Collab_img/raft_logo.png',
    artists: [],
    conversations: [],
    partners: [
      { name: 'The Raft', logo: '/images/Collab_img/raft_logo.png', placeholder: 'Raft' },
    ],
  },
  // ───────────────────────────────────────────────────────────────────
  // COLLAB #2 — Let There Be Guitar (April 2026)
  // ───────────────────────────────────────────────────────────────────
  {
    id: 1,
    slug: 'let-there-be-guitar-2026',
    eventName: 'Let There Be Guitar',
    tagline: 'A celebration of Indian guitar mastery — rock, jazz-fusion, funk, and soul — all under one roof.',
    date: 'April 30, 2026',
    venue: 'The Humming Tree',
    city: 'Bengaluru',
    description: 'FretPractice was proud to be part of the second edition of "Let There Be Guitar" — a celebration of Indian guitar mastery held at the iconic Humming Tree in Bengaluru. As a community partner, we connected with incredible artists, sharing conversations about musicianship, practice, and the future of guitar technology. The evening brought together legends and rising stars across rock, jazz-fusion, funk, and soul.',
    bannerImage: '/images/Collab_img/LTBG_Logo.png',
    artists: [
      {
        id: 1,
        name: 'Chandresh Kudwa',
        role: "Guitar Virtuoso · DAD n' Daughter",
        bio: "With nearly 30 years of professional experience, Chandresh Kudwa is one of India's most respected guitarists. From his work with bands like Freedom, Dream Out Loud, and Nexus, to collaborating with international legends like Jennifer Batten (Michael Jackson's guitarist), Gilby Clarke (Guns N' Roses), and Indian maestros like A.R. Rahman and Shankar-Ehsaan-Loy — Chandresh has done it all. He also runs The Guitar Thing, his own guitar education platform, proving his dedication to nurturing the next generation of players.",
        highlights: [
          '~30 years of professional guitar experience',
          'Collaborated with A.R. Rahman, Jennifer Batten, Gilby Clarke',
          'Session musician for top Bollywood composers',
          'Runs The Guitar Thing — guitar education platform',
          'FGN Guitars endorsed artist',
        ],
        image: '/images/Collab_img/chandresh.png',
        socials: [
          { platform: 'instagram', url: 'https://www.instagram.com/chandreshkudwa/' },
          { platform: 'youtube', url: 'https://www.youtube.com/@ChandreshKudwa' },
        ],
      },
      {
        id: 2,
        name: 'Chirag Todi',
        role: 'Songwriter · Composer · Guitarist',
        bio: "Ahmedabad-based Chirag Todi is a genre-defying force in Indian independent music. As a founding member of Heat Sink and a collaborator with A.R. Rahman and Clinton Cerejo, his musical range spans jazz, blues, funk, neo-soul, and Hindustani classical. His debut album Bangers Only (2024) is a testament to his versatility. Winner of the Nexa Music Award and Clef Music Award, named Best Pop Artist of the Year by Indian Music Diaries, a voting member of the Recording Academy, and featured on the cover of Rolling Stone India — Chirag is the real deal.",
        highlights: [
          'Collaborated with A.R. Rahman & Clinton Cerejo',
          'Winner of Nexa Music Award & Clef Music Award',
          'Best Pop Artist of the Year — Indian Music Diaries',
          'Voting member of the Recording Academy',
          'Featured on Rolling Stone India cover',
          "Spotify's Radar India playlist artist",
        ],
        image: '/images/Collab_img/chirag.png',
        socials: [
          { platform: 'instagram', url: 'https://www.instagram.com/chiragtodi/' },
          { platform: 'youtube', url: 'https://www.youtube.com/@ChiragTodi' },
        ],
      },
    ],
    conversations: [
      {
        id: 1,
        artist: 'Chandresh Kudwa',
        title: 'In Conversation with Chandresh Kudwa',
        description: 'We sat down with guitar legend Chandresh Kudwa to talk about his 30-year journey, practice discipline, the art of collaboration, and what he thinks about the future of tools like FretPractice for musicians.',
        thumbnail: null,
        youtubeUrl: 'https://www.youtube.com/watch?v=vXJmY0RxHH4',
        youtubeEmbedId: 'vXJmY0RxHH4',
      },
      {
        id: 2,
        artist: 'Chirag Todi',
        title: 'In Conversation with Chirag Todi',
        description: 'Chirag Todi shared his thoughts on songwriting, his genre-blending approach, working with A.R. Rahman, and how structured practice tools like FretPractice can help the next generation of musicians find their voice.',
        thumbnail: null,
        youtubeUrl: 'https://www.youtube.com/@FretPractice',
        youtubeEmbedId: null,
      },
    ],
    partners: [
      { name: 'Let There Be Guitar', logo: '/images/Collab_img/LTBG_Logo.png', placeholder: 'LTBG' },
      { name: 'Gigital India', logo: '/images/Collab_img/GigitalIndia.png', placeholder: 'GI' },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // COLLAB #3 — Upcoming Music Festival in Kolkata
  // ───────────────────────────────────────────────────────────────────
  {
    id: 3,
    slug: 'upcoming-music-festival-kolkata',
    eventName: 'Upcoming Music Festival',
    tagline: 'Coming soon to the cultural capital of India.',
    date: 'TBA',
    venue: 'TBD',
    city: 'Kolkata',
    description: 'FretPractice is heading to Kolkata. We are partnering with one of the citys most anticipated upcoming music festivals — bringing exclusive feature and product launches, alongside some of the biggest names in music. Stay tuned for artist announcements and everything we have in store.',
    bannerImage: null,
    artists: [],
    conversations: [],
    partners: [],
  },
]

// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useWindowWidth(): number {
  const [w, setW] = useState(0)
  useEffect(() => {
    const fn = () => setW(window.innerWidth)
    fn()
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])
  return w
}


function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect() } },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [threshold])
  return { ref, inView }
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────

function GlobalStyles() {
  useEffect(() => {
    const s = document.createElement('style')
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');
      *,*::before,*::after { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body { margin: 0; padding: 0; background: #000; color: rgba(255,255,255,0.88); font-family: 'DM Sans', sans-serif; font-weight: 300; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
      body::after { content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 9998; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.05'/%3E%3C/svg%3E"); opacity: 0.28; }
      ::-webkit-scrollbar { width: 5px; }
      ::-webkit-scrollbar-track { background: #000; }
      ::-webkit-scrollbar-thumb { background: rgba(37,89,244,0.35); border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(37,89,244,0.6); }
      @keyframes riseIn { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes breathePulse { 0%,100% { box-shadow: 0 0 0 4px rgba(37,89,244,0.2); } 50% { box-shadow: 0 0 0 8px rgba(37,89,244,0.06); } }
      @keyframes floatA { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(35px,-25px) scale(1.04); } 66% { transform: translate(-20px,30px) scale(0.97); } }
      @keyframes floatB { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-28px,18px) scale(1.03); } 66% { transform: translate(22px,-32px) scale(0.98); } }
    `
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])
  return null
}

// ─── SHARED UI COMPONENTS ─────────────────────────────────────────────────────

function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const { ref, inView } = useInView()
  return (
    <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: `opacity .85s cubic-bezier(.16,1,.3,1) ${delay}s, transform .85s cubic-bezier(.16,1,.3,1) ${delay}s` }}>
      {children}
    </div>
  )
}

function HRule() {
  return <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)' }} />
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 22 }}>
      <span style={{ display: 'block', width: 24, height: 1, background: '#2559F4' }} />
      {children}
      <span style={{ display: 'block', width: 24, height: 1, background: '#2559F4' }} />
    </div>
  )
}

function SectionHeading({ line1, line2 }: { line1: string; line2: string }) {
  const w = useWindowWidth()
  const isMobile = w < 768
  return (
    <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(36px,8vw,56px)' : 'clamp(44px,5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', margin: '0 auto 18px' }}>
      {line1}{' '}
      <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{line2}</span>
    </h2>
  )
}

// ─── HERO ─────────────────────────────────────────────────────────────────────

function CollabHero() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const totalArtists = COLLABORATIONS.reduce((acc, c) => acc + c.artists.length, 0)
  const totalEvents = COLLABORATIONS.length
  return (
    <section style={{ minHeight: isMobile ? '52vh' : '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '100px 20px 56px' : '120px 52px 80px', position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
      {/* Ambient glows */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: 800, height: 800, top: -300, left: '50%', transform: 'translateX(-50%)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,89,244,0.2) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'floatA 22s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: 500, height: 500, bottom: -200, right: -100, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,33,74,0.6) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'floatB 28s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '44px 44px', maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 70%)' }} />
      </div>

      <div style={{ animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .1s both' }}>
        <Eyebrow>Collaborations</Eyebrow>
      </div>

      <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(44px,10vw,72px)' : 'clamp(60px,8vw,108px)', fontWeight: 400, lineHeight: 0.92, letterSpacing: '0.04em', color: '#fff', maxWidth: 820, margin: '0 auto 28px', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .2s both' }}>
        Beyond the screen.<br />
        <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Into the scene.</span>
      </h1>

      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 15 : 18, color: 'rgba(255,255,255,0.44)', fontWeight: 300, lineHeight: 1.78, maxWidth: 560, margin: '0 auto 36px', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .32s both' }}>
        FretPractice isn't just an app — it's a movement. We partner with live events, collaborate with legendary artists, and support the guitar community from stage to screen.
      </p>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: isMobile ? 24 : 48, animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .44s both' }}>
        {[
          { val: String(totalEvents), label: totalEvents === 1 ? 'Event Partnership' : 'Event Partnerships' },
          { val: String(totalArtists), label: 'Artists Featured' },
          { val: String(COLLABORATIONS.reduce((a, c) => a + c.conversations.length, 0)), label: 'Conversations' },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 28 : 36, color: '#fff', letterSpacing: '0.04em', lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── SOCIAL ICON ──────────────────────────────────────────────────────────────

function SocialIcon({ platform }: { platform: string }) {
  const paths: Record<string, string> = {
    instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
    youtube: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    twitter: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  }
  return (
    <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'currentColor' }}>
      <path d={paths[platform] || ''} />
    </svg>
  )
}

function SocialLink({ href, label, platform }: { href: string; label: string; platform: string }) {
  const [hov, setHov] = useState(false)
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-hover
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: hov ? 'rgba(37,89,244,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${hov ? 'rgba(37,89,244,0.35)' : 'rgba(255,255,255,0.1)'}`, color: hov ? '#fff' : 'rgba(255,255,255,0.4)', transform: hov ? 'translateY(-2px)' : 'none', transition: 'all .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer', textDecoration: 'none' }}>
      <SocialIcon platform={platform} />
    </a>
  )
}

// ─── ARTIST CARD ──────────────────────────────────────────────────────────────

function ArtistCard({ artist, delay }: { artist: ArtistData; delay: number }) {
  const [hov, setHov] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const w = useWindowWidth()
  const isMobile = w < 768
  const initials = artist.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()

  return (
    <Reveal delay={delay}>
      <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ background: hov ? '#0d1223' : '#0a0d17', border: `1px solid ${hov ? 'rgba(37,89,244,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 22, overflow: 'hidden', transition: 'background .4s, border-color .35s, transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s', transform: hov ? 'translateY(-6px)' : 'none', boxShadow: hov ? '0 24px 72px rgba(0,0,0,0.55), 0 0 40px rgba(37,89,244,0.08)' : 'none', cursor: 'pointer', position: 'relative' }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Top accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, transparent)', transform: hov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .45s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />

        {/* Image / Placeholder */}
        <div style={{ position: 'relative', width: '100%', paddingBottom: '85%', overflow: 'hidden', background: 'linear-gradient(145deg, #111827, #0a0d17)' }}>
          {artist.image ? (
            <img src={artist.image} alt={artist.name} draggable="false"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', transition: 'transform .6s cubic-bezier(.16,1,.3,1), filter .4s', transform: hov ? 'scale(1.05)' : 'scale(1)', userSelect: 'none', filter: artist.image.includes('chirag') ? 'grayscale(1)' : 'none' }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(145deg, #111827, #0d1223)' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(37,89,244,0.06) 1px, transparent 1px)', backgroundSize: '24px 24px', opacity: 0.6 }} />
              <div style={{ width: isMobile ? 70 : 90, height: isMobile ? 70 : 90, borderRadius: '50%', background: 'linear-gradient(145deg, rgba(37,89,244,0.18), rgba(37,89,244,0.06))', border: '1px solid rgba(37,89,244,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 26 : 32, letterSpacing: '0.04em', color: '#2559F4', position: 'relative', zIndex: 1, marginBottom: 12 }}>{initials}</div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.15)', textTransform: 'uppercase', letterSpacing: '0.1em', position: 'relative', zIndex: 1 }}>Photo coming soon</div>
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(to top, rgba(10,13,23,0.96), transparent)', pointerEvents: 'none' }} />
        </div>

        {/* Card Content */}
        <div style={{ padding: isMobile ? '22px 22px 24px' : '26px 28px 28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '4px 12px', marginBottom: 14 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2559F4', display: 'block' }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#2559F4' }}>{artist.role}</span>
          </div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 24 : 28, letterSpacing: '0.04em', color: '#fff', lineHeight: 1.05, marginBottom: 14 }}>{artist.name}</div>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 13 : 13.5, lineHeight: 1.78, color: 'rgba(255,255,255,0.44)', fontWeight: 300, margin: '0 0 18px', display: '-webkit-box', WebkitLineClamp: expanded ? 999 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{artist.bio}</p>

          {expanded && (
            <div style={{ marginBottom: 18, animation: 'riseIn .4s ease both' }}>
              {artist.highlights.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <svg viewBox="0 0 24 24" fill="none" style={{ width: 15, height: 15, flexShrink: 0, marginTop: 3 }}>
                    <circle cx="12" cy="12" r="10" fill="rgba(37,89,244,0.15)" stroke="rgba(37,89,244,0.4)" strokeWidth="1.5" />
                    <polyline points="8 12 11 15 16 9" stroke="#7eb8ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.5)', fontWeight: 300, lineHeight: 1.5 }}>{h}</span>
                </div>
              ))}
            </div>
          )}

          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
            style={{ background: 'none', border: 'none', fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, color: '#2559F4', cursor: 'pointer', padding: 0, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 4 }}>
            {expanded ? 'Show less' : 'Read more'}
            <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: '#2559F4', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .3s' }}><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" /></svg>
          </button>

          <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 18 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {artist.socials?.map((s, i) => <SocialLink key={i} href={s.url} label={s.platform} platform={s.platform} />)}
          </div>
        </div>
      </div>
    </Reveal>
  )
}

// ─── CONVERSATION CARD ────────────────────────────────────────────────────────

function ConversationCard({ convo, delay }: { convo: ConversationData; delay: number }) {
  const [hov, setHov] = useState(false)
  const w = useWindowWidth()
  const isMobile = w < 768
  const hasEmbed = !!convo.youtubeEmbedId

  // If there's a real embed ID, render inline YouTube player + card info below
  // Otherwise render the placeholder thumbnail with a link
  const Wrapper = hasEmbed ? 'div' : 'a'
  const wrapperProps = hasEmbed
    ? {}
    : { href: convo.youtubeUrl || '#', target: '_blank', rel: 'noopener noreferrer' }

  return (
    <Reveal delay={delay}>
      <Wrapper {...(wrapperProps as any)} data-hover
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ display: 'block', background: hov ? '#0d1223' : '#0a0d17', border: `1px solid ${hov ? 'rgba(37,89,244,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 20, overflow: 'hidden', textDecoration: 'none', transition: 'background .4s, border-color .35s, transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s', transform: hov ? 'translateY(-4px)' : 'none', boxShadow: hov ? '0 20px 60px rgba(0,0,0,0.5), 0 0 30px rgba(37,89,244,0.06)' : 'none', cursor: 'pointer', position: 'relative' }}>
        {/* Top accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #ff0000, transparent)', transform: hov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .45s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />

        {/* Video / Thumbnail area */}
        {hasEmbed ? (
          <div style={{ width: '100%', position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
            <iframe
              src={`https://www.youtube.com/embed/${convo.youtubeEmbedId}?rel=0&modestbranding=1`}
              title={convo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        ) : (
          <div style={{ width: '100%', height: isMobile ? 180 : 220, background: 'linear-gradient(135deg, #111827, #0d1223, #0a0d17)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,0,0,0.04) 1px, transparent 1px)', backgroundSize: '28px 28px', opacity: 0.6 }} />
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: hov ? 'rgba(255,0,0,0.9)' : 'rgba(255,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1, transition: 'background .3s, transform .3s', transform: hov ? 'scale(1.1)' : 'scale(1)', boxShadow: '0 8px 32px rgba(255,0,0,0.3)' }}>
              <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: '#fff', marginLeft: 3 }}><path d="M8 5v14l11-7z" /></svg>
            </div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 12, position: 'relative', zIndex: 1 }}>Coming soon</div>
          </div>
        )}

        <div style={{ padding: isMobile ? '20px 20px 24px' : '24px 28px 28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,0,0,0.1)', border: '1px solid rgba(255,0,0,0.2)', borderRadius: 100, padding: '4px 12px', marginBottom: 14 }}>
            <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: '#ff4444' }}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ff6666' }}>YouTube · {convo.artist}</span>
          </div>
          <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 22 : 26, letterSpacing: '0.04em', color: '#fff', lineHeight: 1.1, marginBottom: 12 }}>{convo.title}</h3>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.75, color: 'rgba(255,255,255,0.42)', fontWeight: 300, margin: 0 }}>{convo.description}</p>
          <a href={convo.youtubeUrl || '#'} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16, fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: hov ? '#fff' : '#2559F4', transition: 'color .3s', textDecoration: 'none' }}>
            {hasEmbed ? 'Watch on YouTube' : 'Coming soon — Watch on YouTube'}
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'currentColor', transform: hov ? 'translateX(4px)' : 'translateX(0)', transition: 'transform .3s' }}><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
          </a>
        </div>
      </Wrapper>
    </Reveal>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// COLLABORATION CARD — Uniform card representing one partnership.
// ═══════════════════════════════════════════════════════════════════════

function CollaborationCard({ collab }: { collab: CollaborationEvent }) {
  const [cardHov, setCardHov] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const isMobile = useWindowWidth() < 768

  return (
    <div data-hover onMouseEnter={() => setCardHov(true)} onMouseLeave={() => setCardHov(false)}
      style={{
        background: cardHov ? 'rgba(17,24,39,0.95)' : 'rgba(10,13,23,0.95)',
        border: `1px solid ${cardHov ? 'rgba(37,89,244,0.4)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 24,
        overflow: 'hidden',
        transition: 'background .4s, border-color .35s, box-shadow .4s, transform .4s',
        transform: cardHov ? 'translateY(-6px)' : 'none',
        boxShadow: cardHov ? '0 24px 72px rgba(0,0,0,0.55), 0 0 40px rgba(37,89,244,0.08)' : '0 12px 32px rgba(0,0,0,0.3)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: isMobile ? 360 : 420,
        boxSizing: 'border-box',
      }}>
      {/* Top accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, transparent)', transform: cardHov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .5s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />

      {/* Banner */}
      <div style={{ width: '100%', height: isMobile ? 100 : 130, background: 'linear-gradient(135deg, #0a0d17 0%, #111827 40%, #0d1223 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        {collab.bannerImage ? (
          <img src={collab.bannerImage} alt={collab.eventName} draggable="false" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none', padding: 16, boxSizing: 'border-box' }} />
        ) : (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(37,89,244,0.08) 1px, transparent 1px)', backgroundSize: '32px 32px', opacity: 0.5 }} />
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6, position: 'relative', zIndex: 1 }}>
              <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: '#2559F4' }}>
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
            </div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 13, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', position: 'relative', zIndex: 1 }}>Upcoming music festival in Kolkata</div>
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? '16px' : '22px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Date + Venue Badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '3px 10px' }}>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, color: '#7eb8ff' }}>{collab.date}</span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 100, padding: '3px 10px' }}>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>{collab.venue}, {collab.city}</span>
          </div>
        </div>

        <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 20 : 24, fontWeight: 600, color: '#fff', lineHeight: 1.15, marginBottom: 10 }}>{collab.eventName}</h3>
        <p style={{
          fontFamily: "'DM Sans',sans-serif",
          fontSize: isMobile ? 12.5 : 13,
          lineHeight: 1.6,
          color: 'rgba(255,255,255,0.44)',
          fontWeight: 300,
          margin: '0 0 12px',
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 999 : 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>{collab.description}</p>

        {collab.description.length > 140 && (
          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
            style={{
              background: 'none',
              border: 'none',
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 11,
              fontWeight: 500,
              color: '#2559F4',
              cursor: 'pointer',
              padding: 0,
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
            {expanded ? 'Show less' : 'Read more'}
            <svg viewBox="0 0 24 24" style={{ width: 11, height: 11, fill: '#2559F4', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .3s' }}>
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
            </svg>
          </button>
        )}

        {/* Partners inside card */}
        {collab.partners.length > 0 && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 14, marginTop: 'auto' }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 9.5, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 6 }}>Partners</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {collab.partners.map((p, idx) => (
                <div key={idx} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, padding: '4px 10px', height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title={p.name}>
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} style={{ maxHeight: 18, maxWidth: 70, objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.2)' }}>{p.placeholder}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── MORE COLLABS CTA ─────────────────────────────────────────────────────────

function MoreCollabsCTA() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const [hov, setHov] = useState(false)

  return (
    <section style={{ padding: isMobile ? '80px 0 100px' : '120px 0 140px', background: '#0a0d17', position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(37,89,244,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ maxWidth: 600, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, position: 'relative', zIndex: 1 }}>
        <Reveal>
          <Eyebrow>Stay Tuned</Eyebrow>
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(36px,8vw,56px)' : 'clamp(44px,5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', marginBottom: 18 }}>
            More collaborations<br />
            <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>coming soon.</span>
          </h2>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, lineHeight: 1.8, color: 'rgba(255,255,255,0.44)', fontWeight: 300, marginBottom: 44 }}>
            We're always looking for the next stage, the next conversation, the next community to be part of. Follow us to stay in the loop.
          </p>
          <a href="https://www.instagram.com/fretpractice/" target="_blank" rel="noopener noreferrer" data-hover
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#2559F4', color: '#fff', textDecoration: 'none', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, padding: '14px 30px', borderRadius: 10, cursor: 'pointer', boxShadow: hov ? '0 0 0 1px rgba(37,89,244,0.7), 0 16px 48px rgba(37,89,244,0.48)' : 'none', transform: hov ? 'translateY(-2px)' : 'none', transition: 'box-shadow .35s cubic-bezier(.16,1,.3,1), transform .3s cubic-bezier(.16,1,.3,1)' }}>
            Follow @FretPractice →
          </a>
        </Reveal>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// PAGE — Renders all collaborations dynamically
// ═══════════════════════════════════════════════════════════════════════

export default function Collaborations() {
  const w = useWindowWidth()
  const isMobile = w < 768

  // Flatten lists for global sections
  const allArtists = COLLABORATIONS.flatMap(c => c.artists)
  const allConversations = COLLABORATIONS.flatMap(c => c.conversations)

  const [activeTab, setActiveTab] = useState(0)
  const scrollerRef = useRef<HTMLDivElement>(null)

  const cardWidth = isMobile ? 260 : 310
  const gap = 24
  const step = cardWidth + gap

  const handleScroll = () => {
    const el = scrollerRef.current
    if (!el) return
    const index = Math.round(el.scrollLeft / step)
    if (index !== activeTab && index >= 0 && index < COLLABORATIONS.length) {
      setActiveTab(index)
    }
  }

  const scrollToCollab = (index: number) => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollTo({
      left: index * step,
      behavior: 'smooth'
    })
    setActiveTab(index)
  }

  useEffect(() => {
    const prev = document.title
    document.title = 'Collaborations — FretPractice'
    const meta = document.querySelector('meta[name="description"]')
    const prevDesc = meta?.getAttribute('content') || ''
    meta?.setAttribute('content', "Explore FretPractice's collaborations with legendary Indian guitarists and musicians. From partnering with live events to conversations with the best in the scene.")
    return () => { document.title = prev; meta?.setAttribute('content', prevDesc) }
  }, [])

  return (
    <>
      <GlobalStyles />
      <Nav />
      <CollabHero />
      <HRule />

      {/* ── Section 1: Partnerships Scroller ── */}
      <section style={{ padding: isMobile ? '60px 0' : '100px 0 80px', background: '#000' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
              <Eyebrow>Event Partnerships</Eyebrow>
              <SectionHeading line1="Stages &" line2="Venues" />
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 520, margin: '0 auto' }}>
                FretPractice partners with live events and music venues to connect the guitar community.
              </p>
            </div>
          </Reveal>

          {/* Navigation Dot Indicators */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, margin: '0px 0 32px' }}>
            {COLLABORATIONS.map((collab, i) => (
              <button
                key={collab.id}
                onClick={() => scrollToCollab(i)}
                style={{
                  width: activeTab === i ? 28 : 10,
                  height: 10,
                  borderRadius: 5,
                  background: activeTab === i ? '#2559F4' : 'rgba(255,255,255,0.2)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
                  padding: 0
                }}
                title={collab.eventName}
              />
            ))}
          </div>

          <Reveal delay={0.1}>
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              style={{
                display: 'flex',
                overflowX: 'auto',
                gap: gap,
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                paddingBottom: 24,
                alignItems: 'stretch',
                justifyContent: w > 1024 ? 'center' : 'flex-start',
              }}
            >
              {COLLABORATIONS.map((collab) => (
                <div key={collab.id} style={{ flex: `0 0 ${cardWidth}px`, scrollSnapAlign: 'start', display: 'flex' }}>
                  <CollaborationCard collab={collab} />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <HRule />

      {/* ── Section 2: Featured Artists Scroller ── */}
      {allArtists.length > 0 && (
        <section style={{ padding: isMobile ? '60px 0' : '80px 0 100px', background: '#0a0d17' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
            <Reveal>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
                <Eyebrow>Featured Artists</Eyebrow>
                <SectionHeading line1="The guitarists who" line2="inspired us." />
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 520, margin: '0 auto' }}>
                  Legends who've shaped the Indian guitar scene — and who believe in what FretPractice is building.
                </p>
              </div>
            </Reveal>

            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: isMobile ? 16 : 24,
              padding: '12px 4px 28px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
            }}>
              {allArtists.map((artist, i) => (
                <div key={artist.id} style={{ flex: isMobile ? '0 0 260px' : '0 0 340px', scrollSnapAlign: 'start' }}>
                  <ArtistCard artist={artist} delay={i * 0.08} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <HRule />

      {/* ── Section 3: YouTube Videos Scroller ── */}
      {allConversations.length > 0 && (
        <section style={{ padding: isMobile ? '60px 0' : '80px 0 100px', background: '#000' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
            <Reveal>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
                <Eyebrow>In Conversation</Eyebrow>
                <SectionHeading line1="Stories from the" line2="stage." />
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 520, margin: '0 auto' }}>
                  We sat down with incredible guitarists to talk about practice, musicianship, and the future of guitar technology.
                </p>
              </div>
            </Reveal>

            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: isMobile ? 16 : 24,
              padding: '12px 4px 28px',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              marginBottom: isMobile ? 32 : 48
            }}>
              {allConversations.map((convo, i) => (
                <div key={convo.id} style={{ flex: isMobile ? '0 0 280px' : '0 0 440px', scrollSnapAlign: 'start' }}>
                  <ConversationCard convo={convo} delay={i * 0.1} />
                </div>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div style={{ textAlign: 'center' }}>
                <a href="https://youtube.com/@fretpractice" target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: '14px 28px', textDecoration: 'none', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.6)', transition: 'all .3s' }}>
                  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: '#ff4444' }}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                  Visit our YouTube Channel →
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <HRule />

      <MoreCollabsCTA />
      <Footer />
    </>
  )
}
